import { useForm } from "react-hook-form";
import {
  FormPhoneInput,
  FormProvider,
  phoneSchema,
} from "@inductiveautomation/ws-components";

import { Profile } from "../Profile.types";
import { useUpdatePhoneNumber } from "../hooks/useUpdatePhoneNumber";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useState } from "react";

const ProfilePhoneField = ({
  phoneNumber,
}: {
  phoneNumber: Profile["phoneNumber"];
}) => {
  const { t } = useTranslation();
  const resolver = yupResolver(
    phoneSchema({
      non_numeric: t("common.inputs.phone.non_numeric_error"),
      not_possible: t("common.inputs.phone.not_possible_error"),
    })
  );

  const values = phoneNumber ?? { countryCode: "US", number: "" };
  const phoneForm = useForm({ resolver, values });
  const [prevValues, setPrevValues] = useState(values);
  const { updatePhoneNumber } = useUpdatePhoneNumber(phoneForm);

  const onSubmitPhoneNumber = () => {
    if (
      prevValues.number === phoneForm.getValues().number &&
      prevValues.countryCode === phoneForm.getValues().countryCode
    ) {
      // don't attempt to update number if the values have not changed since last submitted
      // (to avoid repeated bad update attempts where the value is not saved to profile)
      return;
    }
    return void phoneForm.handleSubmit(async ({ countryCode, number }) => {
      if (values.number === "" && number === "") {
        // NOTE: (2024-11-05) Noah Settersten => If the previous phone number was blank and the new value is also blank,
        // ignore any changes to country code since we can't save only a new country code with an empty phone number.
        return;
      }
      if (number !== values.number || countryCode !== values.countryCode) {
        // NOTE: (2024-12-09) Noah Settersten => Only send an update to the API if either the countryCode or number is
        // different from what is already saved in the profile.
        await updatePhoneNumber({ number, countryCode });
      }
      setPrevValues(phoneForm.getValues());
    })();
  };

  return (
    <FormProvider {...phoneForm}>
      <form
        onBlur={onSubmitPhoneNumber}
        onSubmit={(event) => event.preventDefault()}
      >
        <FormPhoneInput
          prefix="profile-form"
          name="number"
          countryName="countryCode"
          label=""
        />
      </form>
    </FormProvider>
  );
};

export default ProfilePhoneField;
